import React from 'react';
import { FiSearch, FiLoader, FiExternalLink, FiRefreshCw } from 'react-icons/fi';
import { apiService } from '../../service/Api.js';

const ResearchTab = ({ 
  insightData, 
  Section, 
  researchData, 
  setResearchData, 
  researchLoading, 
  setResearchLoading, 
  researchError, 
  setResearchError, 
  hasSearched, 
  setHasSearched 
}) => {

  const performWebResearch = async () => {
    if (!insightData?.prompt_for_web_search) {
      setResearchError('No search prompt available from insight data');
      return;
    }

    setResearchLoading(true);
    setResearchError(null);

    try {
      const response = await apiService.webresearch(insightData.prompt_for_web_search);
      
      if (response.data && response.data.success && response.data.data) {
        setResearchData(response.data.data);
        setHasSearched(true);
      } else {
        setResearchError('No research data received from the search');
      }
    } catch (err) {
      console.error('Web research error:', err);
      setResearchError('Failed to perform web research. Please try again.');
    } finally {
      setResearchLoading(false);
    }
  };

  const renderResearchResults = () => {
    if (!researchData) return null;

    return (
      <div className="space-y-6">
        {/* Title Section */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-200">
          <h2 className="text-xl font-bold text-gray-900 mb-2">
            {researchData.title}
          </h2>
        </div>

        {/* Sections */}
        {researchData.sections && researchData.sections.map((section, index) => (
          <div key={index} className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
            {/* Section Heading */}
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {section.heading}
              </h3>
              {section.subheading && (
                <h4 className="text-md font-medium text-gray-700 mb-3">
                  {section.subheading}
                </h4>
              )}
            </div>

            {/* Section Description */}
            <div className="mb-4">
              <p className="text-gray-600 leading-relaxed">
                {section.description}
              </p>
            </div>

            {/* Call-to-Action Links */}
            {section.cta && section.cta.length > 0 && (
              <div className="border-t border-gray-100 pt-4">
                <div className="flex flex-wrap gap-3">
                  {section.cta.map((ctaItem, ctaIndex) => (
                    <a
                      key={ctaIndex}
                      href={ctaItem.value}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-blue-50 hover:bg-blue-100 text-blue-700 px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 border border-blue-200"
                    >
                      <FiExternalLink className="w-4 h-4" />
                      {ctaItem.label}
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  const renderInitialState = () => (
    <div className="text-center py-12">
      <div className="bg-gray-50 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
        <FiSearch className="w-10 h-10 text-gray-400" />
      </div>
      <h3 className="text-lg font-semibold text-gray-900 mb-2">
        Web Research Available
      </h3>
      <p className="text-gray-600 mb-6 max-w-md mx-auto">
        Click the button below to perform web research based on the current company insights.
      </p>
      <button
        onClick={performWebResearch}
        disabled={researchLoading || !insightData?.prompt_for_web_search}
        className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white px-6 py-3 rounded-lg font-medium transition-colors duration-200"
      >
        {researchLoading ? (
          <>
            <FiLoader className="w-5 h-5 animate-spin" />
            Researching...
          </>
        ) : (
          <>
            <FiSearch className="w-5 h-5" />
            Start Web Research
          </>
        )}
      </button>
      {!insightData?.prompt_for_web_search && (
        <p className="text-sm text-red-600 mt-3">
          No search prompt available in insight data
        </p>
      )}
    </div>
  );

  const renderError = () => (
    <div className="text-center py-8">
      <div className="bg-red-50 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
        <FiRefreshCw className="w-8 h-8 text-red-400" />
      </div>
      <h3 className="text-lg font-semibold text-red-900 mb-2">
        Research Failed
      </h3>
      <p className="text-red-600 mb-4">
        {researchError}
      </p>
      <button
        onClick={performWebResearch}
        disabled={researchLoading}
        className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg font-medium transition-colors duration-200"
      >
        <FiRefreshCw className="w-4 h-4" />
        Try Again
      </button>
    </div>
  );

  return (
    <Section
      title="Web Research Intelligence"
      iconComponent={FiSearch}
    >
      <div className="space-y-6">
        {/* Header with Refresh Button */}
        {hasSearched && researchData && (
          <div className="flex justify-between items-center">
            <p className="text-sm text-gray-600">
              Latest research insights from web sources
            </p>
            <button
              onClick={performWebResearch}
              disabled={researchLoading}
              className="inline-flex items-center gap-2 bg-gray-100 hover:bg-gray-200 disabled:bg-gray-50 disabled:cursor-not-allowed text-gray-700 px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200"
            >
              <FiRefreshCw className={`w-4 h-4 ${researchLoading ? 'animate-spin' : ''}`} />
              Refresh Research
            </button>
          </div>
        )}

        {/* Content */}
        {researchError ? (
          renderError()
        ) : researchLoading ? (
          <div className="text-center py-12">
            <FiLoader className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-600" />
            <p className="text-gray-600">Performing web research...</p>
            <p className="text-sm text-gray-500 mt-2">This may take a few moments</p>
          </div>
        ) : hasSearched && researchData ? (
          renderResearchResults()
        ) : (
          renderInitialState()
        )}
      </div>
    </Section>
  );
};

export default ResearchTab;
